/**
 * TaskService — transitions, list, complete, assign, escalate, dependency resolution.
 */

import { TASK_STATUS, normalizeTask, TASK_SOURCE } from '../../workflow/taskModel';
import { loadTasksForResident, saveTasksForResident } from './TaskPersistence';
import { applyOverdueStatus } from './SLAEngine';

/**
 * Resolve blocked/unblocked state based on `dependsOnTemplateIds`.
 * A task is blocked when any of its dependency templateIds is not completed.
 */
function resolveDependencies(tasks) {
  const completedTemplates = new Set(
    tasks
      .filter((t) => t.status === TASK_STATUS.COMPLETED && t.templateId)
      .map((t) => t.templateId)
  );

  return tasks.map((task) => {
    const deps = task.dependsOnTemplateIds;
    if (!deps || deps.length === 0) return task;
    if (task.status === TASK_STATUS.COMPLETED || task.status === TASK_STATUS.CANCELLED) return task;

    const allDone = deps.every((id) => completedTemplates.has(id));
    if (!allDone && task.status !== TASK_STATUS.BLOCKED) {
      const pending = deps.filter((id) => !completedTemplates.has(id));
      return {
        ...task,
        status: TASK_STATUS.BLOCKED,
        blockedReason: `Waiting on: ${pending.join(', ')}`,
        updatedAt: new Date().toISOString(),
      };
    }
    if (allDone && task.status === TASK_STATUS.BLOCKED) {
      return {
        ...task,
        status: TASK_STATUS.PENDING,
        blockedReason: null,
        updatedAt: new Date().toISOString(),
      };
    }
    return task;
  });
}

/**
 * @param {string|number} residentId
 * @param {string} [workflowKey]
 */
export function listTasks(residentId, workflowKey) {
  const tasks = loadTasksForResident(residentId);
  const withDeps = resolveDependencies(tasks);
  const updated = withDeps.map((t) => applyOverdueStatus(t));
  saveTasksForResident(residentId, updated);
  if (workflowKey) {
    return updated.filter((t) => t.workflowKey === workflowKey);
  }
  return updated;
}

/**
 * @param {string|number} residentId
 * @param {string} taskId
 * @param {string} [userId]
 * @param {string} [evidenceNote]
 */
export function completeTask(residentId, taskId, userId = 'current_user', evidenceNote) {
  const tasks = loadTasksForResident(residentId);
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return { ok: false, error: 'not_found' };
  if (tasks[idx].status === TASK_STATUS.BLOCKED) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[TaskService] completeTask rejected — blocked', { taskId, residentId });
    }
    return { ok: false, error: 'blocked', task: tasks[idx] };
  }

  const now = new Date().toISOString();
  const updated = {
    ...tasks[idx],
    status: TASK_STATUS.COMPLETED,
    completedAt: now,
    completedBy: userId,
    evidenceNote: evidenceNote ?? tasks[idx].evidenceNote,
    updatedAt: now,
  };
  tasks[idx] = updated;
  saveTasksForResident(residentId, tasks);

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[TaskService] completeTask', { taskId, residentId });
  }

  return { ok: true, task: updated };
}

/**
 * @param {string|number} residentId
 * @param {string} taskId
 */
export function setTaskInProgress(residentId, taskId) {
  const tasks = loadTasksForResident(residentId);
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return { ok: false, error: 'not_found' };
  if (tasks[idx].status === TASK_STATUS.COMPLETED) return { ok: false, error: 'already_completed' };

  const updated = {
    ...tasks[idx],
    status: TASK_STATUS.IN_PROGRESS,
    updatedAt: new Date().toISOString(),
  };
  tasks[idx] = updated;
  saveTasksForResident(residentId, tasks);
  return { ok: true, task: updated };
}

/**
 * @param {string|number} residentId
 * @param {string} workflowKey
 */
export function completionPercent(residentId, workflowKey) {
  const tasks = listTasks(residentId, workflowKey);
  if (tasks.length === 0) return { percent: 0, completed: 0, total: 0 };
  const completed = tasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length;
  return {
    percent: Math.round((completed / tasks.length) * 100),
    completed,
    total: tasks.length,
  };
}

// ── Undo / Reopen ─────────────────────────────────────────────────────

/**
 * Reopen a completed or cancelled task back to pending.
 * Records who reopened and why in the audit trail.
 */
export function reopenTask(residentId, taskId, userId = 'current_user', reason = '') {
  const tasks = loadTasksForResident(residentId);
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return { ok: false, error: 'not_found' };

  const task = tasks[idx];
  if (task.status !== TASK_STATUS.COMPLETED && task.status !== TASK_STATUS.CANCELLED) {
    return { ok: false, error: 'not_completed', task };
  }

  const now = new Date().toISOString();
  const prevNote = task.evidenceNote || '';
  const reopenNote = `[Reopened by ${userId} at ${now}${reason ? `: ${reason}` : ''}] ${prevNote}`.trim();

  tasks[idx] = {
    ...task,
    status: TASK_STATUS.PENDING,
    completedAt: null,
    completedBy: null,
    evidenceNote: reopenNote,
    updatedAt: now,
    _reopenHistory: [
      ...(task._reopenHistory || []),
      {
        previousStatus: task.status,
        previousCompletedBy: task.completedBy,
        previousCompletedAt: task.completedAt,
        reopenedBy: userId,
        reopenedAt: now,
        reason,
      },
    ],
  };
  saveTasksForResident(residentId, tasks);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[TaskService] reopenTask', { taskId, residentId, reason });
  }
  return { ok: true, task: tasks[idx] };
}

// ── Notes ─────────────────────────────────────────────────────────────

/**
 * Add or update a note on a task without changing its status.
 */
export function addTaskNote(residentId, taskId, note, userId = 'current_user') {
  const tasks = loadTasksForResident(residentId);
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return { ok: false, error: 'not_found' };

  const now = new Date().toISOString();
  const existing = tasks[idx].evidenceNote || '';
  const appended = existing
    ? `${existing}\n[${userId} — ${now}] ${note}`
    : `[${userId} — ${now}] ${note}`;

  tasks[idx] = {
    ...tasks[idx],
    evidenceNote: appended,
    updatedAt: now,
  };
  saveTasksForResident(residentId, tasks);
  return { ok: true, task: tasks[idx] };
}

// ── Assignment API ────────────────────────────────────────────────────

/**
 * Assign a task to a specific user.
 */
export function assignTask(residentId, taskId, assigneeUserId, assignedBy = 'system') {
  const tasks = loadTasksForResident(residentId);
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return { ok: false, error: 'not_found' };

  tasks[idx] = {
    ...tasks[idx],
    assigneeUserId,
    updatedAt: new Date().toISOString(),
  };
  saveTasksForResident(residentId, tasks);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[TaskService] assignTask', { taskId, assigneeUserId, assignedBy });
  }
  return { ok: true, task: tasks[idx] };
}

/**
 * Create a manual (ad-hoc) task for a resident.
 */
export function createManualTask(residentId, taskData) {
  const task = normalizeTask({
    ...taskData,
    residentId,
    source: TASK_SOURCE.MANUAL,
  });

  const tasks = loadTasksForResident(residentId);
  tasks.push(task);
  saveTasksForResident(residentId, tasks);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[TaskService] createManualTask', { residentId, taskId: task.id });
  }
  return { ok: true, task };
}

/**
 * Cancel a task.
 */
export function cancelTask(residentId, taskId, reason = '') {
  const tasks = loadTasksForResident(residentId);
  const idx = tasks.findIndex((t) => t.id === taskId);
  if (idx === -1) return { ok: false, error: 'not_found' };

  tasks[idx] = {
    ...tasks[idx],
    status: TASK_STATUS.CANCELLED,
    evidenceNote: reason || tasks[idx].evidenceNote,
    updatedAt: new Date().toISOString(),
  };
  saveTasksForResident(residentId, tasks);
  return { ok: true, task: tasks[idx] };
}

// ── Escalation ────────────────────────────────────────────────────────

/**
 * Escalate overdue tasks to Facility Manager by bumping priority.
 * Returns list of escalated task IDs.
 */
export function escalateOverdueTasks(residentId, escalatedBy = 'system') {
  const tasks = loadTasksForResident(residentId);
  const escalated = [];
  const now = new Date().toISOString();

  tasks.forEach((task, idx) => {
    if (task.status !== TASK_STATUS.OVERDUE) return;
    if (task.priority === 'critical' && task.ownerRole === 'Facility Manager') return;

    tasks[idx] = {
      ...task,
      priority: 'critical',
      ownerRole: 'Facility Manager',
      evidenceNote: `Escalated from ${task.ownerRole || 'unassigned'} by ${escalatedBy} at ${now}. ${task.evidenceNote || ''}`.trim(),
      updatedAt: now,
    };
    escalated.push(task.id);
  });

  if (escalated.length > 0) {
    saveTasksForResident(residentId, tasks);
    if (process.env.NODE_ENV === 'development') {
      console.debug('[TaskService] escalated', { residentId, count: escalated.length });
    }
  }
  return escalated;
}

// ── Stage Transition ──────────────────────────────────────────────────

/**
 * Check if 7A settling-in is complete (all tasks done or day >= 28).
 * If so, return transition readiness info.
 */
export function checkSettlingTransition(residentId, admissionDateIso) {
  const tasks = listTasks(residentId, 'stage7a_settling');
  if (tasks.length === 0) return { ready: false, reason: 'no_tasks' };

  const completed = tasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length;
  const percent = Math.round((completed / tasks.length) * 100);

  const admission = new Date(admissionDateIso);
  admission.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysSinceAdmission = Math.floor((today - admission) / 86400000);

  const allCompleted = completed === tasks.length;
  const pastDay28 = daysSinceAdmission >= 28;

  return {
    ready: allCompleted || pastDay28,
    reason: allCompleted ? 'all_tasks_completed' : pastDay28 ? 'day_28_reached' : 'in_progress',
    daysSinceAdmission,
    percent,
    completed,
    total: tasks.length,
    pendingTasks: tasks.filter((t) => t.status !== TASK_STATUS.COMPLETED).map((t) => ({
      id: t.id,
      title: t.title,
      status: t.status,
    })),
  };
}

/**
 * Get a summary of all task statistics for a resident across all workflows.
 */
export function getResidentTaskSummary(residentId) {
  const allTasks = loadTasksForResident(residentId);
  const byWorkflow = {};

  allTasks.forEach((t) => {
    const key = t.workflowKey || 'unassigned';
    if (!byWorkflow[key]) {
      byWorkflow[key] = { total: 0, completed: 0, overdue: 0, blocked: 0, inProgress: 0, pending: 0 };
    }
    byWorkflow[key].total++;
    if (t.status === TASK_STATUS.COMPLETED) byWorkflow[key].completed++;
    else if (t.status === TASK_STATUS.OVERDUE) byWorkflow[key].overdue++;
    else if (t.status === TASK_STATUS.BLOCKED) byWorkflow[key].blocked++;
    else if (t.status === TASK_STATUS.IN_PROGRESS) byWorkflow[key].inProgress++;
    else if (t.status === TASK_STATUS.PENDING) byWorkflow[key].pending++;
  });

  return {
    total: allTasks.length,
    completed: allTasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length,
    overdue: allTasks.filter((t) => t.status === TASK_STATUS.OVERDUE).length,
    blocked: allTasks.filter((t) => t.status === TASK_STATUS.BLOCKED).length,
    byWorkflow,
  };
}
